import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { LINKS, PORTRAITS, scrollToId } from "../data/content";
import { WhatsAppIcon } from "./icons";

const lineReveal = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.25 + i * 0.16 },
  }),
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const portraitY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden pt-[100px] pb-16">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#0D3B3E]/[0.06]" />
        <div className="absolute left-1/4 top-0 bottom-0 w-px bg-[#0D3B3E]/[0.04] hidden lg:block" />
        <div className="absolute right-1/4 top-0 bottom-0 w-px bg-[#0D3B3E]/[0.04] hidden lg:block" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
        <motion.div style={{ y: textY }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-status-pill"
            className="inline-flex items-center gap-2.5 border border-[#0D9488]/40 bg-[#0D9488]/[0.06] rounded-full px-4 py-2 mb-8"
          >
            <span className="status-dot w-1.5 h-1.5 rounded-full bg-[#0D9488]" />
            <span className="text-[10px] sm:text-[11px] font-mono tracking-[0.2em] text-[#0D3B3E]">
              [ STATUS: ACCEPTING PATIENTS / MUMBAI BASED ]
            </span>
          </motion.div>

          <h1
            data-testid="hero-doctor-name"
            className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.02] text-[#0D3B3E]"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={lineReveal} initial="hidden" animate="visible" className="block">
                DR. MUGDHA
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span custom={1} variants={lineReveal} initial="hidden" animate="visible" className="block text-[#0D9488]">
                ADHIKARI
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl"
          >
            Consulting Homoeopathic Physician —{" "}
            <span className="text-[#0D3B3E] font-medium">MD (Homoeopathic Materia Medica), BHMS</span>.
            Gentle, root-cause medicine at Gentle Cure Homoeoclinic, Kandivali West, and online worldwide.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-whatsapp-consult-btn"
              href={LINKS.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-[#0D3B3E] text-[#F8FAF8] text-xs font-mono tracking-[0.18em] rounded-full pl-6 pr-2 py-2 hover:bg-[#0D9488] transition-colors duration-300"
            >
              CONSULT ON WHATSAPP
              <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#2DD4BF]/20 group-hover:bg-[#F8FAF8]/20 transition-colors duration-300">
                <WhatsAppIcon className="w-4 h-4" />
              </span>
            </a>
            <button
              data-testid="hero-credentials-btn"
              onClick={() => scrollToId("academics")}
              className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.18em] text-[#0D3B3E] border border-[#0D3B3E]/25 rounded-full px-6 py-3.5 hover:border-[#0D9488] hover:text-[#0D9488] transition-colors duration-300"
            >
              VIEW CREDENTIALS
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-8 text-[10px] font-mono tracking-[0.22em] text-slate-500"
          >
            MD (HOM. MATERIA MEDICA) · BHMS · GENTLE CURE HOMOEOCLINIC
          </motion.p>
        </motion.div>

        <motion.div style={{ y: portraitY }} className="lg:col-span-5 order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-portrait-frame-fig01"
            className="portrait-frame relative max-w-sm mx-auto lg:ml-auto"
          >
            <span className="corner-tick top-0 left-0 border-t-2 border-l-2 -translate-x-2 -translate-y-2" />
            <span className="corner-tick top-0 right-0 border-t-2 border-r-2 translate-x-2 -translate-y-2" />
            <span className="corner-tick bottom-0 left-0 border-b-2 border-l-2 -translate-x-2 translate-y-2" />
            <span className="corner-tick bottom-0 right-0 border-b-2 border-r-2 translate-x-2 translate-y-2" />
            <div className="overflow-hidden bg-[#0D3B3E]/5">
              <img
                src={PORTRAITS.hero}
                alt="Dr. Mugdha Adhikari at her clinic desk"
                className="w-full aspect-[4/5] object-cover object-top"
                loading="eager"
              />
            </div>
            <div className="flex items-center justify-between mt-4">
              <p className="text-[10px] font-mono tracking-[0.2em] text-slate-500">FIG. 01 — CLINIC PORTRAIT</p>
              <p className="text-[10px] font-mono tracking-[0.2em] text-[#0D9488]">KANDIVALI W, MUMBAI</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
